import type { ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import PrivacyRequests from '@/components/PrivacyRequests';
import { PRIVACY_CONTROLLER, PRIVACY_EMAIL, PRIVACY_UPDATED, formatDay } from '@/lib/privacy';

// What the app keeps about people, why, who sees it and for how long (RGPD
// art. 13). Open to everyone, signed in or not. Every statement here must stay
// true of the code: change this page when what the app keeps or does changes.

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section aria-labelledby={id} className="space-y-2">
      <h2 id={id} className="text-lg font-black text-foreground">
        {title}
      </h2>
      <div className="space-y-2 text-sm leading-relaxed text-foreground">{children}</div>
    </section>
  );
}

function Contact() {
  if (PRIVACY_EMAIL) {
    return (
      <a href={`mailto:${PRIVACY_EMAIL}`} className="font-bold text-primary underline underline-offset-2">
        {PRIVACY_EMAIL}
      </a>
    );
  }
  return <span className="font-bold">[contacto por definir]</span>;
}

/** Where "Voltar" leads: the account screen when signed in, else the login. */
function useBackTo(): string {
  const { isAuthenticated, kind } = useAuthStore();
  if (!isAuthenticated) return '/login';
  return kind === 'guardian' ? '/conta' : '/profile';
}

export default function PrivacyPage() {
  const { isAuthenticated, kind } = useAuthStore();
  const backTo = useBackTo();
  const canRequest = isAuthenticated && (kind === 'participant' || kind === 'guardian');

  return (
    <>
      <Helmet>
        <title>Privacidade | AICD</title>
      </Helmet>
      <div className="mx-auto max-w-2xl space-y-8 px-4 py-6">
        <header className="space-y-3">
          <Link
            to={backTo}
            className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
              <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Voltar
          </Link>
          <div>
            <h1 className="text-2xl font-black text-foreground">Privacidade</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Que dados a app DIGGER guarda, para quê, quem os vê e durante quanto tempo.
            </p>
          </div>
        </header>

        {canRequest && <PrivacyRequests />}

        <Section id="quem" title="Quem trata os seus dados">
          <p>
            O responsável pelos dados é a {PRIVACY_CONTROLLER}. Para qualquer pergunta ou pedido
            sobre os seus dados, escreva para <Contact />
            {canRequest ? ' ou use os botões acima.' : '.'}
          </p>
        </Section>

        <Section id="que-dados" title="Que dados guardamos">
          <p>
            <strong>No registo de um Digger:</strong> nome, apelido, género, data de nascimento,
            email, telemóvel, número do Cartão de Cidadão, habilitações, situação profissional,
            escola, morada e localidade, conforme os preencha. Para menores, também os dados do
            Encarregado de Educação e a sua autorização, e a autorização de uso de imagem.
          </p>
          <p>
            <strong>O que faz nos Centros:</strong> as entradas e saídas, as sessões de atividades
            em que esteve, os badges e certificados que recebeu e o equipamento que usou. É isto que
            forma o seu percurso.
          </p>
          <p>
            <strong>De um Encarregado de Educação:</strong> nome, apelido, email, telemóvel e os
            educandos que a equipa associou à conta.
          </p>
        </Section>

        <Section id="para-que" title="Para que os usamos">
          <ul className="list-disc space-y-1 pl-5">
            <li>Registar as entradas nos Centros e o uso do equipamento.</li>
            <li>Mostrar-lhe o seu percurso, os badges e os certificados.</li>
            <li>Mostrar ao Encarregado de Educação, quando a equipa o associou, se o educando está no Centro, o percurso e os badges.</li>
            <li>Contactá-lo sobre a sua conta, por exemplo para recuperar a palavra-passe.</li>
            <li>Fazer as estatísticas e os relatórios de impacto dos Centros, que contam pessoas e atividades sem as identificar.</li>
          </ul>
          <p>
            Não vendemos os seus dados, não os usamos para publicidade e não os partilhamos com
            outras entidades.
          </p>
          <p>
            Usamos os dados porque deu o seu consentimento no registo e, no caso de menores, com a
            autorização do Encarregado de Educação. Todos os registos são aprovados pela equipa.
          </p>
        </Section>

        <Section id="quem-ve" title="Quem os vê">
          <p>
            A equipa dos Centros DIGGER, para gerir os registos, as atividades e o equipamento. Um
            Encarregado de Educação só vê os educandos que a equipa lhe associou, e só o percurso,
            a presença no Centro e os badges. Ninguém se associa a um educando sozinho.
          </p>
        </Section>

        <Section id="onde" title="Onde ficam">
          <p>
            Num servidor próprio, num Centro em Portugal. Não ficam num serviço de nuvem. Há uma
            cópia de segurança por dia, guardada durante 14 dias.
          </p>
        </Section>

        <Section id="quanto-tempo" title="Durante quanto tempo">
          <ul className="list-disc space-y-1 pl-5">
            <li>Enquanto tiver conta.</li>
            <li>
              Quando a conta é eliminada, os dados pessoais são apagados de vez. Ficam apenas, sem
              nome, as visitas, sessões, badges, o género e o ano de nascimento, para as
              estatísticas. Desaparecem das cópias de segurança ao fim de 14 dias.
            </li>
            <li>Um pedido de conta de Encarregado de Educação que não seja aprovado em 90 dias é apagado.</li>
            <li>Os registos de início de sessão são apagados 30 dias depois de expirarem.</li>
          </ul>
        </Section>

        <Section id="direitos" title="Os seus direitos">
          <p>Pode, a qualquer momento e sem custos:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>pedir uma cópia de todos os seus dados;</li>
            <li>corrigir dados errados;</li>
            <li>pedir que a sua conta e os seus dados sejam eliminados;</li>
            <li>retirar o consentimento ou opor-se a um uso dos seus dados.</li>
          </ul>
          <p>
            Faça o pedido {canRequest ? 'com os botões acima ou ' : ''}por email para <Contact />.
            Respondemos no prazo de um mês. Se achar que os seus dados não foram bem tratados, pode
            apresentar queixa à Comissão Nacional de Proteção de Dados (
            <a href="https://www.cnpd.pt" className="text-primary underline underline-offset-2" target="_blank" rel="noreferrer">
              www.cnpd.pt
            </a>
            ).
          </p>
        </Section>

        <Section id="cookies" title="Cookies e armazenamento">
          <p>
            A app não usa cookies de publicidade nem de estatísticas de visitas. Guarda no seu
            aparelho apenas o que precisa para funcionar: a sua sessão iniciada e uma cópia da
            app para abrir mais depressa e sem rede. Por isso não lhe pedimos autorização para
            cookies.
          </p>
          <p>
            O tipo de letra da app é carregado do Google Fonts, pelo que a Google recebe o endereço
            de internet do seu aparelho quando abre a app.
          </p>
        </Section>

        <p className="text-xs text-muted-foreground">Última atualização: {formatDay(PRIVACY_UPDATED)}.</p>
      </div>
    </>
  );
}
